import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Button, IconButton, ButtonGroup, LinearProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableRow, TableCell } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ChevronLeft, ChevronRight, OpenInNew, OpenInNewOff, Keyboard, ZoomIn, ZoomOut, RotateLeft, RotateRight, Layers, LayersOutlined } from "@mui/icons-material";
import { Document, Page } from "react-pdf";

import { AppDispatch, RootState } from "../../configureStore";
import { IdentifyTask, ReturnActive } from "../../interface/returns";
import { getReturnTaskByKey, getReturnPdfByRef, updateIdentificationJson, saveIdentification, SET_HAS_CHANGES } from "../../redux/reducer/returns";
import IdentifyPageSelector from "../organisms/IdentifyPageSelector";
import { useIdentifySync } from "../../hooks/useIdentifySync";
import usePdfZoom from "../../hooks/usePdfZoom";
import { recordTaskReady, recordPdfLoaded } from "../../utils/taskPdfTiming";

const PDF_BASE_WIDTH = 700;

// Configure PDF.js worker

// localStorage utilities
const STORAGE_KEY_PREFIX = 'identify_pages_';
const STORAGE_EXPIRY_DAYS = 7;

interface LocalStorageData {
    identificationJson: any[];
    currentPage: number;
    visitedPages: number[];
    timestamp: number;
}

const getLocalStorageKey = (key: string): string => `${STORAGE_KEY_PREFIX}${key}`;

const saveToLocalStorage = (key: string, data: LocalStorageData): void => {
    try {
        localStorage.setItem(getLocalStorageKey(key), JSON.stringify(data));
    } catch (error) {
        console.error('Failed to save to localStorage:', error);
    }
};

const loadFromLocalStorage = (key: string): LocalStorageData | null => {
    try {
        const stored = localStorage.getItem(getLocalStorageKey(key));
        if (!stored) return null;

        const data: LocalStorageData = JSON.parse(stored);
        const now = Date.now();
        const ageInDays = (now - data.timestamp) / (1000 * 60 * 60 * 24);

        // Warn if data is older than expiry period
        if (ageInDays > STORAGE_EXPIRY_DAYS) {
            console.warn(`localStorage data is ${ageInDays.toFixed(1)} days old, ignoring`);
            clearLocalStorage(key);
            return null;
        }

        return data;
    } catch (error) {
        console.error('Failed to load from localStorage:', error);
        return null;
    }
};

const clearLocalStorage = (key: string): void => {
    try {
        localStorage.removeItem(getLocalStorageKey(key));
    } catch (error) {
        console.error('Failed to clear localStorage:', error);
    }
};

interface IdentifyPagesParams {
}

type LoadingPhase = 'task' | 'pdf' | null;

const IdentifyPages = ({
}: IdentifyPagesParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>('task');
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const {
        zoomPercent, zoomIn, zoomOut, reset: resetZoom, canZoomIn, canZoomOut, isDefault: isDefaultZoom,
    } = usePdfZoom('identify');
    const [selectorOnly, setSelectorOnly] = useState(false);
    const [rotations, setRotations] = useState<Map<number, number>>(new Map());
    const [rotateAllPages, setRotateAllPages] = useState(false);
    const [showHotkeys, setShowHotkeys] = useState(false);
    const [showBackDialog, setShowBackDialog] = useState(false);
    const key = searchParams.get('key');
    const pdfParam = searchParams.get('pdf');

    const pdfOnly = useMemo<boolean>(() => (pdfParam === 'true'), [pdfParam]);

    const activeReturnTask = useSelector<RootState, ReturnActive | undefined>(({ returns }) => returns.activeReturnTask);
    const identificationJson = useSelector<RootState, any[] | undefined>(({ returns }) => returns.identificationJson);
    const hasChanges = useSelector<RootState, boolean>(({ returns }) => returns.hasChanges);
    const isSaving = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['returns/SAVE_IDENTIFICATION']);
    const activePdf = useSelector<RootState, string | undefined>(({ returns }) => returns.activePdf);
    const identifyTask = activeReturnTask?.type === 'identify' ? activeReturnTask as IdentifyTask : undefined;

    const pdfUrl = activePdf ?? null;

    useEffect(() => {
        if (!key) {
            navigate('/returns');
            return;
        }

        setLoadingPhase('task');
        dispatch(getReturnTaskByKey({
            input: { key },
            onSuccess: (response: { data: IdentifyTask }) => {
                const { data } = response;

                recordTaskReady(key, {
                    taskType: data.type,
                    messageId: data.task?.message?.messageId,
                    correlationId: data.task?.message?.correlationId,
                    FICode: data.task?.message?.FICode,
                    loanNumber: data.task?.message?.loanNumber,
                });

                // Try to restore from localStorage
                const savedData = loadFromLocalStorage(key);
                if (savedData) {
                    dispatch(updateIdentificationJson(savedData.identificationJson));
                    setCurrentPage(savedData.currentPage);
                    setVisitedPages(new Set(savedData.visitedPages));
                }

                // Start PDF fetch
                if (data?.blobs?.pdfRef) {
                    setLoadingPhase('pdf');
                    setDownloadProgress(0);
                    dispatch(getReturnPdfByRef({
                        input: {
                            container: data.blobs.pdfRef.container,
                            path: data.blobs.pdfRef.path,
                        },
                        onProgress: (progress: number) => setDownloadProgress(progress),
                        onSuccess: () => {
                            setLoadingPhase(null);
                        },
                        onError: () => {
                            console.error('Failed to fetch PDF');
                            setLoadingPhase(null);
                        },
                    }));
                } else {
                    setLoadingPhase(null);
                }
            },
            onError: () => {
                console.error('Failed to fetch return task');
                navigate('/returns');
            },
        }));
    }, [key, dispatch, navigate]);

    useEffect(() => {
        // Initialize hasChanges to false when component mounts
        dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
    }, [dispatch]);

    const handleDocumentLoadSuccess = useCallback(({ numPages: pages }: { numPages: number }) => {
        setNumPages(pages);
        recordPdfLoaded(key);

        // Populate identificationJson with entries for all PDF pages if needed
        if (identificationJson && pages > identificationJson.length) {
            const existingPages = new Set(identificationJson.map((entry) => entry.pageNumber));
            const updatedJson = [...identificationJson];

            // Add missing pages
            for (let i = 1; i <= pages; i++) {
                if (!existingPages.has(i)) {
                    updatedJson.push({
                        pageNumber: i,
                        isOnList: false,
                        isStateTaxReturn: false,
                        taxYear: new Date().getFullYear(),
                        confidence: 0,
                        reasoning: "",
                    });
                }
            }

            // Sort by pageNumber
            updatedJson.sort((a, b) => a.pageNumber - b.pageNumber);
            dispatch(updateIdentificationJson(updatedJson));
        }
    }, [identificationJson, dispatch, key]);

    const stateReturnPages = useMemo(() => {
        const pages = new Set<number>();
        identificationJson?.forEach((e) => {
            if (e.isStateTaxReturn) pages.add(e.pageNumber);
        });
        return pages;
    }, [identificationJson]);

    const handleNextPage = useCallback(() => {
        setCurrentPage((prev) => {
            const candidate = prev + 1;
            if (candidate > numPages) return prev;
            // Find next non-state-return page
            const result = Array.from({ length: numPages - candidate + 1 }, (_, i) => candidate + i)
                .find((p) => !stateReturnPages.has(p));
            return result ?? Math.min(candidate, numPages);
        });
    }, [numPages, stateReturnPages]);

    const handlePreviousPage = useCallback(() => {
        setCurrentPage((prev) => {
            const candidate = prev - 1;
            if (candidate < 1) return prev;
            // Find previous non-state-return page
            const result = Array.from({ length: candidate }, (_, i) => candidate - i)
                .find((p) => !stateReturnPages.has(p));
            return result ?? Math.max(candidate, 1);
        });
    }, [stateReturnPages]);

    const handleOpenPdf = useCallback(() => {
        const params = new URLSearchParams(searchParams);
        params.set('pdf', 'true');
        window.open(`${window.location.pathname}?${params.toString()}`, '_blank');
        setSelectorOnly(true);
    }, [searchParams]);

    const handleExternalPageChange = useCallback((pageNumber: number) => {
        setCurrentPage(pageNumber);
    }, []);

    const applyRotation = useCallback((delta: number) => {
        setRotations((prev) => {
            const current = prev.get(currentPage) ?? 0;
            const next = (current + delta + 360) % 360;
            const updated = new Map(prev);
            if (rotateAllPages) {
                for (let p = 1; p <= numPages; p++) {
                    updated.set(p, next);
                }
            } else {
                updated.set(currentPage, next);
            }
            return updated;
        });
    }, [currentPage, numPages, rotateAllPages]);

    const handleRotateLeft = useCallback(() => {
        applyRotation(-90);
    }, [applyRotation]);

    const handleRotateRight = useCallback(() => {
        applyRotation(90);
    }, [applyRotation]);

    const toggleRotateAllPages = useCallback(() => {
        setRotateAllPages((prev) => !prev);
    }, []);

    // Set up cross-tab synchronization
    useIdentifySync({
        currentPage,
        onPageChange: handleExternalPageChange,
    });

    const handleUpdateIdentificationJson = useCallback((updatedData: any[]) => {
        dispatch(updateIdentificationJson(updatedData));
    }, [dispatch]);

    const handleBackClick = useCallback(() => {
        if (hasChanges) {
            setShowBackDialog(true);
        } else {
            navigate("/returns");
        }
    }, [hasChanges, navigate]);

    const markUserLeaving = useCallback(async () => {
        if (!key) return;
        try {
            await fetch(`${import.meta.env.VITE_ENDPOINT}/returns/mark-exit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${sessionStorage.getItem('ACCESS_TOKEN')}`,
                    'OCP-APIM-Subscription-Key': import.meta.env.VITE_OCP_APIM_SUB_KEY ?? '',
                },
                body: JSON.stringify({ key }),
            });
        } catch (error) {
            console.error('Failed to mark user exit:', error);
        }
    }, [key]);

    const handleBackConfirm = useCallback(async () => {
        await markUserLeaving();
        setShowBackDialog(false);
        navigate("/returns");
    }, [navigate, markUserLeaving]);

    const handleSaveAsDraft = useCallback(() => {
        if (identificationJson && key) {
            dispatch(saveIdentification({
                input: { identificationJson, key, draft: true },
                onSuccess: () => {
                    setShowBackDialog(false);
                    navigate("/returns");
                },
            }));
        }
    }, [identificationJson, key, dispatch, navigate]);

    // Track which pages have been visited
    const [visitedPages, setVisitedPages] = useState<Set<number>>(new Set([1]));

    useEffect(() => {
        setVisitedPages((prev) => {
            const newSet = new Set(prev);
            newSet.add(currentPage);
            return newSet;
        });
    }, [currentPage]);

    // Listen for storage events from other tabs
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (!key || event.key !== getLocalStorageKey(key)) return;

            if (event.newValue) {
                try {
                    const data: LocalStorageData = JSON.parse(event.newValue);
                    dispatch(updateIdentificationJson(data.identificationJson));
                    setCurrentPage(data.currentPage);
                    setVisitedPages(new Set(data.visitedPages));
                } catch (error) {
                    console.error('Failed to parse storage event data:', error);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [key, dispatch]);

    // Save to localStorage whenever identification data or navigation changes
    useEffect(() => {
        if (key && identificationJson && identificationJson.length > 0) {
            saveToLocalStorage(key, {
                identificationJson,
                currentPage,
                visitedPages: Array.from(visitedPages),
                timestamp: Date.now(),
            });
        }
    }, [identificationJson, currentPage, visitedPages, key]);

    // Save changes to localStorage only (used by "Save Changes" button)
    const handleSaveToLocalStorage = useCallback(() => {
        if (!identificationJson || !key) return;

        saveToLocalStorage(key, {
            identificationJson,
            currentPage,
            visitedPages: Array.from(visitedPages),
            timestamp: Date.now(),
        });

        // Reset hasChanges flag since changes are saved locally
        dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
    }, [identificationJson, key, currentPage, visitedPages, dispatch]);

    // Confirm and save to backend (used by "Confirm All" button)
    const handleConfirm = useCallback(() => {
        if (!identificationJson || !key) return;
        dispatch(saveIdentification({
            input: {
                identificationJson,
                key,
            },
            onSuccess: () => {
                // Clear localStorage on successful backend save
                clearLocalStorage(key);
                dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
                navigate("/returns");
            },
        }));
    }, [identificationJson, key, dispatch, navigate]);

    // Count of pages that require visiting (excludes state tax returns)
    const requiredPageCount = useMemo(() => {
        const stateReturnCount = identificationJson?.filter((e) => e.isStateTaxReturn).length ?? 0;
        return numPages - stateReturnCount;
    }, [numPages, identificationJson]);

    // Check if all non-state-tax-return pages have been visited
    const allPagesVisited = useMemo(() => {
        if (numPages === 0) return false;
        if (requiredPageCount === 0) return true;
        return visitedPages.size >= requiredPageCount;
    }, [visitedPages, numPages, requiredPageCount]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Only handle if not typing in an input/textarea
            if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                if (event.shiftKey) {
                    handleRotateLeft();
                } else {
                    handlePreviousPage();
                }
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                if (event.shiftKey) {
                    handleRotateRight();
                } else {
                    handleNextPage();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleNextPage, handlePreviousPage, handleRotateLeft, handleRotateRight]);

    if (loadingPhase || !identifyTask) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Typography variant="h6" color="text.secondary">
                    {loadingPhase === 'task' ? 'Loading task data...' : 'Loading PDF...'}
                </Typography>
                {loadingPhase === 'pdf' && downloadProgress > 0 && (
                    <Box sx={{ width: '400px', textAlign: 'center' }}>
                        <LinearProgress
                            variant="determinate"
                            value={downloadProgress}
                            sx={{ mb: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {downloadProgress}% downloaded
                        </Typography>
                    </Box>
                )}
                {(loadingPhase === 'task' || downloadProgress === 0) && <CircularProgress />}
            </Box>
        );
    }

    if (!pdfUrl) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" color="error">
                    Failed to load PDF
                </Typography>
                <Button onClick={handleBackClick}>
                    Back to Tasks
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            {!pdfOnly && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">
                        Identify Pages - Loan {identifyTask.task.message.loanNumber} - {identifyTask.task.message.identity.fileName}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        {selectorOnly && (
                            <ButtonGroup size="small" variant="outlined">
                                <Button onClick={() => setSelectorOnly(false)} title="Show PDF">
                                    <OpenInNewOff fontSize="small" />
                                </Button>
                            </ButtonGroup>
                        )}
                        <Tooltip title="View keyboard shortcuts">
                            <IconButton
                                onClick={() => setShowHotkeys(true)}
                                size="small"
                            >
                                <Keyboard />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleConfirm}
                            disabled={!allPagesVisited || isSaving}
                        >
                            {isSaving ? "Confirming..." : allPagesVisited ? "Confirm All" : `View All Pages (${visitedPages.size}/${requiredPageCount})`}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleBackClick}
                        >
                            Back to Tasks
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Main Content */}
            <Box sx={{ flex: 1, overflow: "hidden" }}>
                <Grid container sx={{ height: "100%" }}>
                    {/* PDF Viewer - Left Side */}
                    {!selectorOnly && (
                        <Grid size={pdfOnly ? 12 : 8} sx={{ height: "100%", display: "flex", flexDirection: "column", borderRight: pdfOnly ? 0 : 1, borderColor: "divider" }}>
                            {/* Page Navigation - Top */}
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <ButtonGroup>
                                            <IconButton
                                            onClick={handlePreviousPage}
                                            disabled={currentPage <= 1}
                                            size="small"
                                            >
                                                <ChevronLeft />
                                            </IconButton>
                                            <IconButton
                                            onClick={handleNextPage}
                                            disabled={currentPage >= numPages}
                                            size="small"
                                            >
                                                <ChevronRight />
                                            </IconButton>
                                        </ButtonGroup>
                                        <Typography variant="body2">
                                            Page {currentPage} of {numPages}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Tooltip title="Rotate left (Shift+←)">
                                            <IconButton onClick={handleRotateLeft} size="small">
                                                <RotateLeft />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Rotate right (Shift+→)">
                                            <IconButton onClick={handleRotateRight} size="small">
                                                <RotateRight />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={rotateAllPages ? "Rotation applies to all pages" : "Rotation applies to this page only"}>
                                            <IconButton
                                                onClick={toggleRotateAllPages}
                                                size="small"
                                                color={rotateAllPages ? "primary" : "default"}
                                            >
                                                {rotateAllPages ? <Layers /> : <LayersOutlined />}
                                            </IconButton>
                                        </Tooltip>
                                        <Box sx={{ width: 8 }} />
                                        <Tooltip title="Zoom out">
                                            <span>
                                                <IconButton onClick={zoomOut} disabled={!canZoomOut} size="small">
                                                    <ZoomOut />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Reset zoom">
                                            <span>
                                                <IconButton
                                                    onClick={resetZoom}
                                                    disabled={isDefaultZoom}
                                                    size="small"
                                                    sx={{ minWidth: 48 }}
                                                >
                                                    <Typography variant="caption">{zoomPercent}%</Typography>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Zoom in">
                                            <span>
                                                <IconButton onClick={zoomIn} disabled={!canZoomIn} size="small">
                                                    <ZoomIn />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {!pdfOnly && (
                                            <IconButton
                                                onClick={handleOpenPdf}
                                                size="small"
                                                title="Open PDF in new tab"
                                            >
                                                <OpenInNew />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                                <LinearProgress
                                variant="determinate"
                                value={(currentPage / numPages) * 100}
                                sx={{
                                    height: 6,
                                    borderRadius: 1,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: allPagesVisited ? 'success.main' : 'primary.main',
                                    },
                                }}
                                />
                            </Box>

                            {/* PDF Content */}
                            <Box sx={{ flex: 1, overflow: "auto", p: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Document
                                file={pdfUrl}
                                onLoadSuccess={handleDocumentLoadSuccess}
                                loading={<CircularProgress />}
                                >
                                    <Page
                                    pageNumber={currentPage}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    width={PDF_BASE_WIDTH * (zoomPercent / 100)}
                                    rotate={rotations.get(currentPage) ?? 0}
                                    />
                                </Document>
                            </Box>
                        </Grid>
                    )}

                    {/* Selector Panel - Right Side */}
                    {!pdfOnly && (
                        <Grid size={4} sx={{ height: "100%", overflow: "auto" }}>
                            <Box sx={{ p: 2, height: "100%" }}>
                                {identificationJson && (
                                <IdentifyPageSelector
                                    currentPageNumber={currentPage}
                                    totalPages={numPages}
                                    identificationData={identificationJson}
                                    onUpdate={handleUpdateIdentificationJson}
                                    onSave={handleSaveToLocalStorage}
                                    hasChanges={hasChanges}
                                    isSaving={isSaving}
                                />
                            )}
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Hotkeys Dialog */}
            <Dialog open={showHotkeys} onClose={() => setShowHotkeys(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Keyboard Shortcuts</DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Navigation</TableCell>
                                <TableCell />
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>← / →</TableCell>
                                <TableCell>Previous / Next page</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, pt: 2 }}>Rotation</TableCell>
                                <TableCell />
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>Shift + ←</TableCell>
                                <TableCell>Rotate page left (counter-clockwise)</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>Shift + →</TableCell>
                                <TableCell>Rotate page right (clockwise)</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, pt: 2 }}>Form Fields</TableCell>
                                <TableCell />
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>F</TableCell>
                                <TableCell>Focus on Form Type selector</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>P</TableCell>
                                <TableCell>Focus on Page Number input</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>Y</TableCell>
                                <TableCell>Focus on Tax Year input</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 4 }}>Tab</TableCell>
                                <TableCell>Navigate between form fields</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>

            {/* Back Dialog */}
            <Dialog open={showBackDialog} onClose={() => setShowBackDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        You have unsaved changes. Are you sure you want to go back? All unsaved progress will be lost.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBackDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveAsDraft}
                        variant="outlined"
                        color="primary"
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save as Draft"}
                    </Button>
                    <Button
                        onClick={handleBackConfirm}
                        variant="contained"
                        color="error"
                    >
                        Go Back
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IdentifyPages;
